/**
 * listDataSanitizer.ts - Comprehensive data sanitization for list registry
 * 
 * Ensures all list data is safe for React rendering by:
 * 1. Filtering out schema definitions
 * 2. Converting objects to strings where needed
 * 3. Validating data types
 * 4. Providing detailed logging
 */

import type { ListData } from '../types';

export interface SanitizationResult {
  data: ListData;
  warnings: string[];
  errors: string[];
}

/**
 * Check if a value is a schema definition object
 */
function isSchemaObject(value: any): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  
  // Common schema indicators
  const schemaKeys = ['type', 'required', 'indexed', 'format', 'enum', 'pattern', 'minimum', 'maximum'];
  const hasSchemaKey = schemaKeys.some(key => key in value);
  
  // Config object indicators
  const configKeys = ['strategy', 'ttl', 'maxAge', 'staleWhileRevalidate', 'cache'];
  const hasConfigKey = configKeys.some(key => key in value);
  
  return hasSchemaKey || hasConfigKey;
}

/**
 * Safely convert any value to a primitive or string
 */
function toPrimitive(value: any, fieldName: string, warnings: string[]): any {
  if (value === null || value === undefined) {
    return value;
  }
  
  if (typeof value !== 'object') {
    return value;
  }
  
  if (Array.isArray(value)) {
    return value;
  }
  
  if (isSchemaObject(value)) {
    warnings.push(`Field '${fieldName}' contains schema/config object, converting to string`);
    try {
      return JSON.stringify(value);
    } catch (e) {
      warnings.push(`Failed to stringify '${fieldName}': ${e}`);
      return '[Object]';
    }
  }
  
  // For other objects, stringify them
  warnings.push(`Field '${fieldName}' contains object, converting to string`);
  try {
    return JSON.stringify(value);
  } catch (e) {
    warnings.push(`Failed to stringify '${fieldName}': ${e}`);
    return '[Object]';
  }
}

/**
 * Sanitize a single list object
 */
export function sanitizeListData(list: any, listId?: string): SanitizationResult {
  const warnings: string[] = [];
  const errors: string[] = [];
  const id = listId || list.id || 'unknown';
  
  try {
    // Validate required fields
    if (!list.id) {
      errors.push(`List missing 'id' field`);
    }
    
    // Sanitize all fields
    const sanitized: ListData = {
      id: toPrimitive(list.id, 'id', warnings) || id,
      name: toPrimitive(list.name, 'name', warnings) || id,
      shortName: toPrimitive(list.shortName, 'shortName', warnings),
      version: toPrimitive(list.version, 'version', warnings) || '1.0.0',
      type: toPrimitive(list.type, 'type', warnings) || 'fixed',
      description: toPrimitive(list.description, 'description', warnings) || '',
      source: toPrimitive(list.source, 'source', warnings) || '',
      lastUpdated: toPrimitive(list.lastUpdated, 'lastUpdated', warnings) || new Date().toISOString().split('T')[0],
      color: toPrimitive(list.color, 'color', warnings) || '#6b7280',
      pattern: toPrimitive(list.pattern, 'pattern', warnings) || 'solid',
      icon: toPrimitive(list.icon, 'icon', warnings) || '📋',
      defaultIcon: toPrimitive(list.defaultIcon, 'defaultIcon', warnings) || null,
      allowOther: Boolean(list.allowOther),
      children: Array.isArray(list.children) ? list.children : [],
      metadata: {}, // Always empty for safety
      tags: Array.isArray(list.tags) ? list.tags : undefined,
      query: toPrimitive(list.query, 'query', warnings),
      refresh: toPrimitive(list.refresh, 'refresh', warnings),
      ttl: typeof list.ttl === 'number' ? list.ttl : undefined,
      sourceFile: toPrimitive(list.sourceFile, 'sourceFile', warnings),
      sourceUrl: toPrimitive(list.sourceUrl, 'sourceUrl', warnings)
    };
    
    return { data: sanitized, warnings, errors };
  } catch (error) {
    errors.push(`Fatal error sanitizing list: ${error}`);
    
    // Return minimal safe object
    return {
      data: {
        id,
        name: id,
        version: '1.0.0',
        type: 'fixed',
        description: 'Error loading list',
        source: '',
        lastUpdated: new Date().toISOString().split('T')[0],
        color: '#6b7280',
        pattern: 'solid',
        icon: '⚠️',
        defaultIcon: null,
        allowOther: false,
        children: [],
        metadata: {}
      },
      warnings,
      errors
    };
  }
}

/**
 * Sanitize an array of lists with comprehensive logging
 */
export function sanitizeListRegistry(lists: any[]): {
  sanitized: ListData[];
  totalWarnings: number;
  totalErrors: number;
  details: Map<string, { warnings: string[]; errors: string[] }>;
} {
  const sanitized: ListData[] = [];
  const details = new Map<string, { warnings: string[]; errors: string[] }>();
  let totalWarnings = 0;
  let totalErrors = 0;
  
  for (const list of lists) {
    const result = sanitizeListData(list, list.id);
    sanitized.push(result.data);
    
    if (result.warnings.length > 0 || result.errors.length > 0) {
      details.set(result.data.id, {
        warnings: result.warnings,
        errors: result.errors
      });
      totalWarnings += result.warnings.length;
      totalErrors += result.errors.length;
    }
  }
  
  // Log summary
  if (totalWarnings > 0 || totalErrors > 0) {
    console.warn(`[ListDataSanitizer] Processed ${lists.length} lists: ${totalWarnings} warnings, ${totalErrors} errors`);
    
    details.forEach((detail, listId) => {
      if (detail.errors.length > 0) {
        console.error(`[ListDataSanitizer] ${listId} errors:`, detail.errors);
      }
      if (detail.warnings.length > 0) {
        console.warn(`[ListDataSanitizer] ${listId} warnings:`, detail.warnings);
      }
    });
  } else {
    console.log(`[ListDataSanitizer] Successfully processed ${lists.length} lists with no issues`);
  }
  
  return { sanitized, totalWarnings, totalErrors, details };
}
